/* eslint-disable import/order */
import { DialogActions, DialogContent, Grid, Stack } from "@mui/material";
import { MutationStatus } from "@tanstack/react-query";
import { Formik, Form as FormikForm } from "formik";
import { Save, User } from "lucide-react";
import React, { FC } from "react";
import * as yup from "yup";

// COMPONENTS:

import { UserTypeRoleType } from "api/user/types";
import FormikAutocomplete from "components/forms/FormikAutocomplete";
import FormikTextField from "components/forms/FormikTextField";
import FormSection from "components/forms/FormSection";
import { useUserStore } from "components/stores/UserStore";
import UltimateButton from "components/UltimateButton";

export type UserFormValues = {
  organisation: string;
  email: string;
  role: UserTypeRoleType | null;
};

interface IUserForm {
  handleSubmit: (values: UserFormValues) => void;
  handleClose: () => void;
  initialValues?: UserFormValues;
  submitStatus?: MutationStatus;
}

const UserForm: FC<IUserForm> = ({
  handleSubmit,
  handleClose,
  initialValues = {
    organisation: "",
    email: "",
    role: null,
  },
  submitStatus,
}) => {
  const [user] = useUserStore((s) => [s.user]);

  const schema = yup.object({
    email: yup.string().email("Wrong email format").required(),
    role: yup.string().nullable().required(),
  });

  let roleOptions = [
    {
      value: "admin",
      label: "Admin",
    },
  ];

  if (user?.organisation.type === "partner") {
    roleOptions = [
      {
        value: "partner-admin",
        label: "Referral Partner Admin",
      },
      {
        value: "partner-sales-rep",
        label: "Referral Partner Sales Reprensetative",
      },
      {
        value: "partner",
        label: "Referral Partner",
      },
    ];
  }

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={handleSubmit}
    >
      <FormikForm>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 2 }}>
            <FormSection
              title="User Information"
              description="Email and role for this user."
              Icon={User}
            >
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormikAutocomplete
                    label="Role"
                    name="role"
                    options={roleOptions}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FormikTextField name="email" label="Email" />
                </Grid>
              </Grid>
            </FormSection>
          </Stack>
        </DialogContent>

        <DialogActions>
          <UltimateButton
            type="submit"
            text="Save"
            Icon={
              Save as React.ComponentType<{
                size?: number;
                className?: string;
                style?: React.CSSProperties;
              }>
            }
            loading={submitStatus === "loading"}
            sx={{
              minWidth: 150,
              py: 1.5,
            }}
          />
        </DialogActions>
      </FormikForm>
    </Formik>
  );
};

export default UserForm;
